import Link from 'next/link';
import { useEffect, useState } from 'react';
import CreateAccount from '@/organisms/forms/CreateAccount/CreateAccount';
import { useAuth } from '@frontegg/nextjs';
import { styled } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import MuiDivider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useAccess from '@pkg/access/organisations/useAccess';
import * as Users from '@pkg/entities/users';
import { Feature } from '@/lib/enums';

const Divider = styled(MuiDivider)({
  marginTop: '0 !important',
  marginBottom: '0 !important',
});

const ProfileMenuItemText = styled(ListItemText)({
  width: '100%',
  '.MuiTypography-root': {
    fontSize: '0.875rem',
    '&:hover': {
      textDecoration: 'none',
    },
  },
});

const InformationMenuItem = styled(MenuItem)({
  backgroundColor: 'transparent !important',
  marginBottom: '6px',

  '&:hover': {
    cursor: 'default',
    backgroundColor: 'transparent',
  },
});

const MenuPaperStyles = {
  overflow: 'visible',
  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
  mt: 1.5,
  '& .MuiAvatar-root': {
    width: 32,
    height: 32,
    ml: -0.5,
    mr: 0,
  },
};

const ProfileSubMenu = ({
  anchorEl,
  open,
  onClose,
  me,
  organisation,
  showManageProfile = false,
}) => {
  const { tenantsState, user } = useAuth();
  const access = useAccess();
  const [tenants, setTenants] = useState([]);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const userName = `${me?.first_name} ${me?.last_name}`;

  const canCreateNewAccount =
    access.features[Feature.CREATE_NEW_ORGANISATION] && access.model.manage;

  useEffect(() => {
    if (!tenantsState) {
      return;
    }
    const activeTenantId = user?.tenantId;
    const tenantNames = tenantsState.tenants
      .filter(({ tenantId }) => tenantId !== activeTenantId)
      .map(({ tenantId, name }) => ({ tenantId, name }));
    setTenants(tenantNames);
  }, [tenantsState]);

  const handleCreateAccount = (e) => {
    e.preventDefault();
    setShowCreateAccount(true);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        id="profile-menu"
        open={open}
        onClose={onClose}
        onClick={onClose}
        PaperProps={{
          elevation: 0,
          sx: MenuPaperStyles,
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Section */}
        <InformationMenuItem onClick={onClose}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar name={userName} src={me?.avatar} />
            <Stack>
              <Typography variant="subtitle2" noWrap>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {organisation?.name}
              </Typography>
            </Stack>
          </Stack>
        </InformationMenuItem>

        <Divider />

        {/* Manage Profile Link */}
        {showManageProfile && (
          <Link
            href={Users.utils.pathname(me)}
            sx={{ '&:hover': { textDecoration: 'none' } }}
          >
            <MenuItem onClick={onClose}>
              <ProfileMenuItemText>Manage profile</ProfileMenuItemText>
            </MenuItem>
          </Link>
        )}
        {showManageProfile && <Divider />}

        {/* Switch Organization Section */}
        {tenants.length > 0 && (
          <MenuItem disabled sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Switch to...
            </Typography>
          </MenuItem>
        )}
        {tenants.map(({ tenantId, name }) => (
          <Link key={tenantId} href={`/a/${tenantId}`}>
            <MenuItem onClick={onClose}>
              <ProfileMenuItemText>{name}</ProfileMenuItemText>
            </MenuItem>
          </Link>
        ))}
        {canCreateNewAccount && (
          <MenuItem onClick={onClose}>
            <Link
              href="#"
              onClick={(e) => handleCreateAccount(e)}
              sx={{
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              <ProfileMenuItemText>Create new account...</ProfileMenuItemText>
            </Link>
          </MenuItem>
        )}
      </Menu>
      <CreateAccount
        ownerId={me?.id}
        open={showCreateAccount}
        onClose={() => setShowCreateAccount(false)}
      />
    </>
  );
};

export default ProfileSubMenu;
